import React, { useRef, useEffect } from 'react';

interface PreviewFrameProps {
  htmlContent: string;
  onError?: (error: string) => void;
  className?: string;
}

export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  htmlContent,
  onError,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const blobUrlRef = useRef<string>();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      onError?.('Preview iframe not found');
      return;
    }

    try {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      // Replace the iframe's content without creating additional history entries
      if (iframe.contentWindow) {
        iframe.contentWindow.location.replace(url);
      } else {
        iframe.src = url;
      }

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = url;
    } catch (err) {
      onError?.(`Failed to render preview: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [htmlContent, onError]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  return (
    <div className={`relative w-full h-full bg-background overflow-hidden ${className || ''}`}>
      <iframe
        ref={iframeRef}
        className="w-full h-full border-0 bg-white"
        title="HTML Preview"
      />
    </div>
  );
};